import { html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { DimensionDefinition, ModuleDefinition } from "@reporting/rp-types/definition";
import { msg } from "@omegagrid/localize";
import { ContextData, ContextFilter, ContextFilterItem, ContextIndicator, PeriodDimensionType } from "@reporting/rp-types/analytics";
import { createRef, ref } from "lit/directives/ref.js";
import { TreeAdapter, TreeEvent, TreeMenu } from "@omegagrid/tree";
import { dom, OmegaComponent, events } from "@omegagrid/core";
import { CommonPeriodAdapter, DimTreeAdapter, PeriodAdapter, ScenarioTreeAdapter, getDefaultSections } from "../../adapters";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { isPeriodDimension, isScenariosDimension } from "../../common";
import rp from '@reporting/rp-app-host';
import { map } from "lit/directives/map.js";

@customElement('rp-fsection-indicators-filters')
export class IndicatorFiltersSection extends OmegaComponent {

	static styles = [OmegaComponent.styles, css`
		.item {
			display: flex;
			flex-direction: row;
			align-items: center;
			height: 20px;
			margin-bottom: 2px;
		}

		.item > * {
			height: 100%;
		}


		.item og-button.btn-filter {
			border: 1px dashed var(--og-accent-color);
			padding: 2px;
			color: var(--og-text-color);
			flex: 1;
		}

		.item og-tree-dropdown.filter {
			flex: 1;
			border: 1px solid var(--og-accent-color);
		}

		.item rp-filters-relativeperiod.filter {
			flex: 1;
		}
	`];

	@property({type: Object})
	indicator: ContextIndicator;

	@property({type: Object})
	context: ContextData;

	@property({type: Array})
	dimensions: DimensionDefinition[] = [];

	@property({type: Object})
	module: ModuleDefinition;

	private addMenuRef = createRef<TreeMenu>();
	get addMenu() { return this.addMenuRef.value }

	get service() { return rp.ms('analytics') }

	private _dimensionMap: Map<string, DimensionDefinition>;
	private _adapters = new Map<string, TreeAdapter>();
	get dimensionMap() {
		if (this._dimensionMap) return this._dimensionMap;
		this._dimensionMap = new Map((this.dimensions || []).map(dim => [dim.dim_num, dim]));
		return this._dimensionMap;
	}

	getDimensionAdapter(dim: string) {
		let adapter = this._adapters.get(dim);
		if (adapter) return adapter;

		if (dim == 'PC') {
			adapter = new CommonPeriodAdapter(this.context.module as ModuleCode);
		} else if (isPeriodDimension(dim)) {
			adapter = new PeriodAdapter(this.service, this.context.module as ModuleCode, dim as PeriodDimensionType);
		} else if (isScenariosDimension(dim)) {
			adapter = new ScenarioTreeAdapter(this.module);
		} else {
			adapter = new DimTreeAdapter(this.module.module as ModuleCode, dim);
		}

		this._adapters.set(dim, adapter);
		return adapter;
	}

	private _currentIndex: number = -1;

	updated() {
		this.addMenu?.setSourceData(this.filterMenuItems, {selectMode: 'single', checkboxes: false});
		this.layout();
	}

	disconnectedCallback() {
		this.addMenu?.remove();
	}

	get filterMenuItems() {
		return [...this.dimensions.flatMap(d => ([{
			k: d.dim_num,
			v: d.description,
			icon: getDefaultSections().get(isPeriodDimension(d.dim_num) ? 'period' : 'dimensions').icon,
			level: 0,
			collapsed: true,
			props: {dimension: d}
		}, ...(d.properties || []).map(a => ({
			k: `${d.dim_num}:${a.key}`,
			v: a.name,
			level: 1,
			props: {dimension: d} // TODO: implement property
		}))]))];
	}

	async openAddMenu(index: number, elm: HTMLElement) {
		this._currentIndex = index;
		dom.setSize(this.addMenu, {w: elm.offsetWidth});
		await this.addMenu.open(elm, 'right');
	}

	updateItems() {
		this.dispatchEvent(new events.ChangeEvent({value: this.indicator}));
	}

	// filter values changed, the item layout stays the same - the parent must not
	// re-render the list item, otherwise the open dropdown gets destroyed
	updateValues() {
		this.dispatchEvent(new events.ChangeEvent({type: 'valuechange', value: this.indicator}));
	}

	addItem(index: number) {
		this._currentIndex = index;
		this.indicator.filters = this.indicator.filters || [];
		this.indicator.filters.push({dim: null});
		this.updateItems();

	}

	removeItem = (index: number) => {
		this.indicator.filters.splice(index, 1);
		this.updateItems();
	}

	_onRelativeFilterChange = (e: events.ChangeEvent, index: number) => {
		const filter = this.indicator.filters[index];
		filter.items = e.value ? [e.value] : [];
		this.updateValues();
	}

	_onFilterChange = (e: TreeEvent, filter: ContextFilter) => {
		filter.items = e.component.selection.serialize() as ContextFilterItem[];
		this.updateValues();
	}

	_onDimensionSelect = (e: TreeEvent) => {
		const dimension = e.node.properties?.dimension as DimensionDefinition;
		this.indicator.filters[this._currentIndex].dim = dimension.dim_num;
		this.updateItems();
	}

	_getPRValue(filter: ContextFilter) : string {
		if (filter.dim != 'PR') return '';
		if (!filter.items?.length) return '';
		const item = filter.items[0];
		return (Array.isArray(item) ? item[0] : item) || '';
	}

	renderItem = (index: number) => {
		const filter = this.indicator.filters[index];
		const dimension = this.dimensionMap.get(filter.dim);
		return html`
			<div class="item">
				<og-icon icon="filter"></og-icon>
				${filter.dim ? html`
					${filter.dim == 'PR' ? html`
						<rp-filters-relativeperiod
							class="filter"
							.value="${this._getPRValue(filter)}"
							@change="${(e: events.ChangeEvent) => this._onRelativeFilterChange(e, index)}">
						</rp-filters-relativeperiod>
					` : html`
						<og-tree-dropdown
							class="filter"
							color="transparent"
							.caption="${dimension?.description || `<${msg('Unknown dimension')}>`}"
							.adapter="${this.getDimensionAdapter(filter.dim)}"
							.value="${filter.items}"
							@change="${(e: TreeEvent) => this._onFilterChange(e, filter)}"
							cross>
						</og-tree-dropdown>
					`}
				` : html`
					<og-button
						caret="down"
						class="btn-filter"
						color="transparent"
						text="+ ${msg('Add filter')}"
						@click="${(e: PointerEvent) => this.openAddMenu(index, e.target as HTMLElement)}">
					</og-button>
				`}

				<og-button
					.icon="${{icon: 'trash', color: 'red'}}"
					style="flex: 0; min-width: 20px; margin-left: 5px"
					color="tranparent"
					@click="${() => this.removeItem(index)}"
					@mousedown="${(e: Event) => e.stopPropagation()}">
				</og-button>
			</div>
		`;
	}

	render = () => this.indicator ? html`
		${map(this.indicator.filters || [], (_filter, index) => this.renderItem(index))}

		<og-tree-menu ${ref(this.addMenuRef)}
			style="display: none"
			maxHeight="200"
			@select="${this._onDimensionSelect}">
		</og-tree-menu>
	` : '';

	layout = () => {

	}

}
